/**
 * UserProfile - User profile dropdown component
 */
import { memo, useState, useRef, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { User, LogOut, Settings, ChevronDown } from 'lucide-react';

const UserProfile = memo(() => {
  const { user, isLoaded } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isLoaded || !user) {
    return null;
  }

  const handleSignOut = async () => {
    await signOut();
    setShowDropdown(false);
  };

  const handleOpenProfile = () => {
    openUserProfile();
    setShowDropdown(false);
  };

  return (
    <div className="user-profile-container" ref={dropdownRef} data-testid="user-profile">
      <button 
        className="user-profile-btn"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {user.imageUrl ? (
          <img 
            src={user.imageUrl} 
            alt={user.fullName || 'User'} 
            className="user-avatar"
          />
        ) : (
          <div className="user-avatar-placeholder">
            <User size={18} />
          </div>
        )}
        <span className="user-name">
          {user.firstName || user.emailAddresses[0]?.emailAddress?.split('@')[0] || 'Usuario'}
        </span>
        <ChevronDown size={14} className={showDropdown ? 'rotate' : ''} />
      </button>

      {showDropdown && (
        <div className="user-dropdown" data-testid="user-dropdown">
          <div className="user-dropdown-header">
            <div className="user-dropdown-info">
              <span className="user-dropdown-name">{user.fullName || 'Usuario'}</span>
              <span className="user-dropdown-email">
                {user.primaryEmailAddress?.emailAddress}
              </span>
            </div>
          </div>
          
          <div className="user-dropdown-divider" />
          
          <button className="user-dropdown-item" onClick={handleOpenProfile}>
            <Settings size={16} />
            <span>Configuración</span>
          </button>
          
          <button className="user-dropdown-item danger" onClick={handleSignOut}>
            <LogOut size={16} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      )}
    </div>
  );
});

UserProfile.displayName = 'UserProfile';

export default UserProfile;
